/**
 * Business hours, evaluated in the browser.
 *
 * Moved from components/text-widget/utils/index.ts (handleBusinessHour). The
 * server applies business hours too — it answers a message outside them with
 * OUTSIDE_OFFICE_HOURS — but the widget also decides up front whether to show
 * the offline message and hide the composer, so the visitor is not invited to
 * type into a chat nobody will answer. The core needs the same answer.
 *
 * The schedule is expressed in the sub-account's timezone; the visitor's
 * clock is converted into it before comparing.
 */

export interface BusinessHoursSettings {
  enableBusinessHours?: boolean;
  timezone?: string;
  outsideOfficeHoursWelcomeMsg?: string;
  openHours?: Array<{
    daysOfTheWeek: number[];
    hours: Array<{ openHour: string; openMinute: string; closeHour: string; closeMinute: string }>;
  }>;
}

export const isWithinBusinessHours = (settings: BusinessHoursSettings, now: Date = new Date()): boolean => {
  try {
    const openHours = settings.openHours || [];
    const serverTime = new Date(now.toLocaleString('en-US', { timeZone: settings.timezone }));
    const currentTimeInServerTZ = new Date(
      serverTime.getTime() + (serverTime.getTimezoneOffset() - now.getTimezoneOffset()) * 60000,
    );

    for (const day of openHours) {
      if (!day.daysOfTheWeek?.includes(currentTimeInServerTZ.getDay())) continue;
      for (const hours of day.hours || []) {
        const openTime = new Date(currentTimeInServerTZ);
        openTime.setHours(parseInt(hours.openHour, 10), parseInt(hours.openMinute, 10), 0);
        const closeTime = new Date(currentTimeInServerTZ);
        closeTime.setHours(parseInt(hours.closeHour, 10), parseInt(hours.closeMinute, 10), 0);
        if (currentTimeInServerTZ >= openTime && currentTimeInServerTZ <= closeTime) return true;
      }
    }
  } catch (err) {
    // An unparseable schedule must not lock the chat; open is the safe answer.
    console.error('[chat-core] could not evaluate business hours; treating as open', err);
    return true;
  }
  return false;
};

/** The widget's signature, kept for its call sites. */
export const handleBusinessHour = (parsedBusinessOfficeHours: BusinessHoursSettings): boolean =>
  isWithinBusinessHours(parsedBusinessOfficeHours);
